import {ASSETS_PREFIX, URLS, UrlUtils} from '../api/urls';

interface OauthUserInfo {
  id: number;
  email?: string;
  nickname?: string;
  provider: string;
}

interface StripeDashboardInfo {
  dashboard_url?: string;
}

function authRequest(url: string, method: RequestInit['method'], body?: unknown): Promise<Response> {
  const headers: Record<string, string> = {Accept: 'application/json'};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  return fetch(url, {
    method,
    credentials: 'include',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });
}

export default class Oauth {
  private static readonly PROVIDER_ICONS: Record<string, string> = {
    google: 'google',
    instagram: 'instagram',
    microsoft: 'microsoft',
    apple: 'apple',
    facebook: 'facebook'
  };

  private button: HTMLElement;
  private icon: HTMLElement;

  private disposeCalls: Array<() => void> = [];

  private loaded: Promise<undefined | unknown>;
  private userInfo?: OauthUserInfo;

  private popup?: HTMLElement;

  private closePopup?: () => void;

  private signInError?: string;

  // TODO: check if it's possible to navigate to a sphere on a different domain/client without reloading
  // if yes, this can break
  static isEnabled(): boolean {
    const override = UrlUtils.getQueryValueFromUrl('USER_AUTHENTICATION')?.trim().toLowerCase();
    if (override === 'true' || override === 'false') return override === 'true';

    const tag = document.querySelector('meta[name="user-authentication"]');
    return tag?.getAttribute('content')?.trim().toLowerCase() === 'true';
  }

  constructor() {
    this.signInError = Oauth.takeSignInError();
    this.button = document.getElementById('oauth-button');
    this.button.classList.remove('is-hidden');
    {
      this.icon = document.createElement('span');
      this.icon.className = 'oauth-button-icon';
      this.button.appendChild(this.icon);
    }
    {
      const onClick = () => this.click();
      this.button.addEventListener('click', onClick);
      this.disposeCalls.push(() => this.button.removeEventListener('click', onClick));
    }
    this.loaded = this.checkStatus();
    if (this.signInError) {
      const reason = this.signInError;
      this.signInError = undefined;
      this.loaded.then(() => this.showSignInError(reason));
    }
  }

  private static takeSignInError(): string | undefined {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('oauth_error');
    if (!error) return undefined;
    params.delete('oauth_error');
    const query = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
    return error;
  }

  private async loadUserInfo(): Promise<OauthUserInfo> {
    const result = await authRequest(URLS.OAUTH_INFO, 'GET');
    if (result.ok) return (await result.json()) as OauthUserInfo;
    else return undefined;
  }

  private async checkStatus() {
    return new Promise(async (resolve, _reject) => {
      try {
        this.userInfo = await this.loadUserInfo();
        this.updateButtonIcon();
        resolve(undefined);
      } catch (e) {
        resolve(e);
      }
    });
  }

  private updateButtonIcon() {
    const provider = this.userInfo?.provider?.toLowerCase();
    const iconName = provider ? Oauth.PROVIDER_ICONS[provider] : undefined;
    if (iconName) {
      this.icon.style.setProperty('--oauth-provider-icon', `url('${ASSETS_PREFIX}/assets/img/icons/${iconName}.svg')`);
      this.icon.classList.add('oauth-button-icon--provider');
    } else {
      this.icon.classList.remove('oauth-button-icon--provider');
      this.icon.style.removeProperty('--oauth-provider-icon');
    }
  }

  private async signOut() {
    this.hidePopup();
    try {
      await authRequest(URLS.OAUTH_SIGN_OUT, 'POST');
    } finally {
      this.userInfo = undefined;
      this.updateButtonIcon();
    }
  }

  private redirectToDeleteAccount() {
    this.hidePopup();
    this.redirect(URLS.OAUTH_DELETE_ACCOUNT);
  }

  private async requestStripeDashboardUrl(url: string): Promise<string | undefined> {
    const result = await authRequest(url, 'POST', {return_url: window.location.href});
    if (!result.ok) return undefined;
    const info = (await result.json()) as StripeDashboardInfo;
    return info?.dashboard_url;
  }
  private async openStripeDashboard(button: HTMLButtonElement, label: HTMLElement) {
    if (button.disabled) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      let dashboardUrl = await this.requestStripeDashboardUrl(URLS.OAUTH_STRIPE_CUSTOMER_LINK);
      if (!dashboardUrl) {
        label.textContent = 'Setting up…';
        dashboardUrl = await this.requestStripeDashboardUrl(URLS.OAUTH_STRIPE_CUSTOMER_ONBOARD);
      } else {
        label.textContent = 'Opening…';
        this.hidePopup();
        this.navigate(dashboardUrl);
      }
    } catch (e) {
      console.warn('Failed to open the Stripe dashboard', e);
      label.textContent = 'Unavailable, try again';
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  private click() {
    this.loaded.then(() => {
      if (this.userInfo === undefined) this.redirectToSignIn();
      else this.showUserInfo();
    });
  }

  private redirectToSignIn() {
    this.redirect(URLS.OAUTH_SIGN_IN);
  }

  private redirect(template: string) {
    const returnTo = window.location.pathname + window.location.search;
    this.navigate(UrlUtils.insertValueToUrl(template, encodeURIComponent(returnTo)));
  }

  private navigate(url: string) {
    // TODO: decide how to handle spheres embedded as iframes?
    const target = window.top ?? window;
    try {
      target.location.assign(url);
    } catch {
      window.location.assign(url);
    }
  }

  private showSignInError(reason: string) {
    if (this.popup) this.hidePopup();

    const popup = document.createElement('div');
    popup.className = 'oauth-popup oauth-popup--error';

    const title = document.createElement('div');
    title.className = 'oauth-popup-title';
    title.textContent = 'Sign-in failed';
    popup.appendChild(title);

    const text = document.createElement('div');
    text.className = 'oauth-popup-error-reason';
    text.textContent = reason.replace(/_/g, ' ');
    popup.appendChild(text);

    this.presentPopup(popup);
  }

  private showUserInfo() {
    if (this.popup) {
      this.hidePopup();
      return;
    }

    const popup = document.createElement('div');
    popup.className = 'oauth-popup';

    {
      const title = document.createElement('div');
      title.className = 'oauth-popup-title';
      title.textContent = this.userInfo.email ?? this.userInfo.nickname ?? '';
      popup.appendChild(title);
    }

    {
      const provider = document.createElement('div');
      provider.className = 'oauth-popup-provider';

      const providerName = this.userInfo.provider;
      const iconName = Oauth.PROVIDER_ICONS[providerName?.toLowerCase()];
      if (iconName) {
        const icon = document.createElement('span');
        icon.className = 'oauth-popup-provider-icon';
        icon.style.setProperty('--oauth-provider-icon', `url('${ASSETS_PREFIX}/assets/img/icons/${iconName}.svg')`);
        provider.appendChild(icon);
      }

      const text = document.createElement('div');
      text.className = 'oauth-popup-provider-text';

      const label = document.createElement('span');
      label.className = 'oauth-popup-provider-label';
      label.textContent = 'Connected with';
      text.appendChild(label);

      const name = document.createElement('span');
      name.className = 'oauth-popup-provider-name';
      name.textContent = providerName;
      text.appendChild(name);

      provider.appendChild(text);
      popup.appendChild(provider);
    }

    // TODO: only show the stripe button if the client has stripe set up?
    {
      const stripe = document.createElement('button');
      stripe.className = 'oauth-popup-stripe';

      const text = document.createElement('span');
      text.className = 'oauth-popup-stripe-text';
      text.textContent = 'Purchases';
      stripe.appendChild(text);

      stripe.onclick = () => this.openStripeDashboard(stripe, text);
      popup.appendChild(stripe);
    }

    {
      const logOut = document.createElement('button');
      logOut.className = 'oauth-popup-log-out';

      const icon = document.createElement('span');
      icon.className = 'oauth-popup-log-out-icon';
      logOut.appendChild(icon);

      const text = document.createElement('span');
      text.textContent = 'Log out';
      logOut.appendChild(text);

      logOut.onclick = () => this.signOut();
      popup.appendChild(logOut);
    }

    {
      const deleteAccount = document.createElement('button');
      deleteAccount.className = 'oauth-popup-delete-account';
      deleteAccount.textContent = 'Delete account';
      deleteAccount.onclick = () => this.redirectToDeleteAccount();
      popup.appendChild(deleteAccount);
    }

    this.presentPopup(popup);
  }

  private presentPopup(popup: HTMLElement) {
    {
      const rect = this.button.getBoundingClientRect();
      popup.style.position = 'fixed';
      popup.style.top = `${rect.bottom}px`;
      popup.style.right = `${window.innerWidth - rect.right}px`;
    }

    document.body.appendChild(popup);
    this.popup = popup;

    // Keep the popup within the viewport (e.g. on small/mobile screens).
    {
      const margin = 8;
      const rect = popup.getBoundingClientRect();

      if (rect.left < margin) {
        popup.style.right = 'auto';
        popup.style.left = `${margin}px`;
      }

      const bottomOverflow = rect.bottom - (window.innerHeight - margin);
      if (bottomOverflow > 0) {
        const top = Math.max(margin, rect.top - bottomOverflow);
        popup.style.top = `${top}px`;
      }
    }

    const onOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!popup.contains(target) && !this.button.contains(target)) this.hidePopup();
    };
    document.addEventListener('click', onOutsideClick, true);
    this.closePopup = () => document.removeEventListener('click', onOutsideClick, true);
  }

  private hidePopup() {
    this.closePopup?.();
    this.closePopup = undefined;
    this.popup?.remove();
    this.popup = undefined;
  }

  dispose() {
    this.hidePopup();
    this.button.classList.add('is-hidden');
    this.disposeCalls.forEach(it => it());
  }
}
